class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.hasAttribute('data-removing')) return;

      const cartItems = this.closest('cart-items');
      if (!cartItems) return;

      this.setAttribute('data-removing', 'true');
      const button = this.querySelector('button');
      if (button) button.disabled = true;

      Promise.resolve(cartItems.updateQuantity(this.dataset.index, 0)).finally(() => {
        this.removeAttribute('data-removing');
        if (button) button.disabled = false;
      });
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

/** Section instance id from templates/cart.json (not the liquid filename) */
const CART_SECTION_ID = 'cart-items';

function cartDebounce(fn, wait) {
  if (typeof debounce === 'function') return debounce(fn, wait);
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getRoutes() {
  if (typeof window !== 'undefined' && window.routes) return window.routes;
  if (typeof routes !== 'undefined') return routes;
  return null;
}
const CART_SECTION_FALLBACK_IDS = ['cart-items', 'main-cart'];

function cartApiJsUrl(routePath, fallbackPath) {
  const base = (routePath || fallbackPath || '').trim();
  if (!base) return (fallbackPath || '/cart/change') + '.js';
  if (base.endsWith('.js')) return base;
  return base.replace(/\/$/, '') + '.js';
}

/** Same route without .js — often works when loyalty apps block *.js cart endpoints */
function cartHtmlUrl(routePath, fallbackPath) {
  const base = (routePath || fallbackPath || '').trim() || fallbackPath || '/cart';
  return base.replace(/\.js\/?$/, '').replace(/\/$/, '') || fallbackPath || '/cart';
}

/** Absolute /cart?section_id=… URL (avoids relative-path 404 on collection/product pages) */
function getCartPagePathname() {
  const shopRoutes = getRoutes();
  const cartPath = (shopRoutes && shopRoutes.cart_url) ? shopRoutes.cart_url : '/cart';
  try {
    const url = cartPath.startsWith('http')
      ? new URL(cartPath)
      : new URL(cartPath.startsWith('/') ? cartPath : '/' + cartPath, window.location.origin);
    return url.pathname;
  } catch (e) {
    return '/cart';
  }
}

function buildCartSectionFetchUrl(sectionId) {
  const url = new URL(getCartPagePathname(), window.location.origin);
  url.searchParams.set('section_id', sectionId);
  return url.toString();
}

function stripMoneyHtml(value) {
  if (value == null) return '';
  const str = String(value);
  if (!str.includes('<')) return str;
  const div = document.createElement('div');
  div.innerHTML = str;
  return (div.textContent || div.innerText || '').trim();
}

function formatCartMoney(cents) {
  if (typeof Woolman !== 'undefined' && Woolman.Utils && Woolman.Utils.formatMoney) {
    return stripMoneyHtml(Woolman.Utils.formatMoney(cents));
  }
  const value = Number(cents);
  if (Number.isNaN(value)) return '0.00';
  return (value / 100).toFixed(2);
}

function setMoneyOnElement(el, cents, prefix) {
  if (!el) return;
  const formatted = formatCartMoney(cents);
  if (String(formatted).includes('<')) {
    el.innerHTML = (prefix || '') + formatted;
  } else {
    el.textContent = (prefix || '') + formatted;
  }
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    // helper variables
    this.cartTotalValue = 0;
    this.cartTotalDiscount = 0;
    this.cartOriginalPrice = 0;

    // elements
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this._quantityRequestSeq = 0;
    this._quantityUpdateQueue = Promise.resolve();
  }

  connectedCallback() {
    if (this._cartItemsBound) return;
    this._cartItemsBound = true;

    this.debouncedOnChange = cartDebounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange);
    this._onQtyButtonClick = this.handleQuantityButtonClick.bind(this);
    this.addEventListener('click', this._onQtyButtonClick, true);

    this._onDiscountClick = this.handleDiscountClick.bind(this);
    this._onDiscountKeydown = this.handleDiscountKeydown.bind(this);
    document.addEventListener('click', this._onDiscountClick);
    document.addEventListener('keydown', this._onDiscountKeydown);

    if (document.documentElement.classList.contains('template-cart')) {
      this._pageshowHandler = (event) => {
        if (event.persisted) this.renderItems();
      };
      window.addEventListener('pageshow', this._pageshowHandler);
    }

    if (!CartItems._globalAjaxBound) {
      CartItems._globalAjaxBound = true;
      document.addEventListener('ajaxProduct:added', CartItems.handleAjaxProductAdded);
    }

    this.updateShippingNotification('cart-shipping-notification');
  }

  disconnectedCallback() {
    if (this._pageshowHandler) {
      window.removeEventListener('pageshow', this._pageshowHandler);
    }
    if (this._onDiscountClick) {
      document.removeEventListener('click', this._onDiscountClick);
    }
    if (this._onDiscountKeydown) {
      document.removeEventListener('keydown', this._onDiscountKeydown);
    }
  }

  handleDiscountClick(event) {
    const button = event.target.closest('[data-fy-discount-apply]');
    if (!button || !this.getCartSectionRoot().contains(button)) return;
    event.preventDefault();
    const block = button.closest('[data-fy-discount]');
    const input = block && block.querySelector('[data-fy-discount-input]');
    if (!input) return;
    this.applyDiscountCode((input.value || '').trim(), block);
  }

  handleDiscountKeydown(event) {
    if (event.key !== 'Enter') return;
    const input = event.target.closest('[data-fy-discount-input]');
    if (!input || !this.getCartSectionRoot().contains(input)) return;
    event.preventDefault();
    const block = input.closest('[data-fy-discount]');
    this.applyDiscountCode((input.value || '').trim(), block);
  }

  showDiscountMessage(block, text, isError) {
    if (!block) return;
    const messageEl = block.querySelector('[data-fy-discount-message]');
    if (!messageEl) return;
    messageEl.textContent = text || '';
    messageEl.classList.toggle('hide', !text);
    messageEl.classList.toggle('fy-cart-discount__message--error', Boolean(isError));
  }

  async applyDiscountCode(code, block) {
    if (!getRoutes()) return;

    const applyBtn = block && block.querySelector('[data-fy-discount-apply]');
    const strings = window.fyCartDrawerStrings || {};

    if (applyBtn) applyBtn.disabled = true;

    try {
      const response = await this.cartPostJson(this.getCartUpdateUrl(), {
        discount: code,
        sections: CART_SECTION_ID,
        sections_url: getCartPagePathname(),
      });
      const responseJson = await response.json();

      if (this.isCartChangeError(response, responseJson)) {
        const message =
          (responseJson && (responseJson.description || responseJson.message)) ||
          strings.discountError ||
          'This discount code is not valid.';
        this.showDiscountMessage(block, message, true);
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:update', { detail: responseJson }));

      const sectionHtml =
        responseJson.sections &&
        (responseJson.sections[CART_SECTION_ID] || responseJson.sections['main-cart']);

      if (sectionHtml) {
        const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
        this.updateCartSections(html);
      } else {
        this.syncShippingFromCartJson(responseJson);
        this.applyCartTotalsFromJson(responseJson);
      }

      const successMessage = code
        ? strings.discountApplied || 'Discount applied'
        : strings.discountRemoved || 'Discount removed';
      const activeBlock = this.getCartSectionRoot().querySelector('[data-fy-discount]');
      this.showDiscountMessage(activeBlock || block, successMessage, false);
    } catch (error) {
      console.error(error);
      this.showDiscountMessage(block, strings.discountError || 'Could not apply discount code.', true);
    } finally {
      if (applyBtn) applyBtn.disabled = false;
    }
  }

  handleQuantityButtonClick(event) {
    const button = event.target.closest('.quantity__button');
    if (!button || !this.contains(button)) return;

    const lineItem = button.closest('.cart-item');
    if (lineItem && (lineItem.classList.contains('is-processing') || lineItem.classList.contains('is-removing'))) return;

    const input = button.closest('quantity-input') && button.closest('quantity-input').querySelector('.quantity__input');
    if (!input || input.getAttribute('name') !== 'updates[]' || !input.dataset.index) return;

    event.preventDefault();
    event.stopPropagation();

    const previous = parseInt(input.value, 10) || 0;
    const min = parseInt(input.min, 10) || 0;
    const next = button.name === 'plus' ? previous + 1 : Math.max(min, previous - 1);
    if (next === previous) return;

    this._skipNextQtyChange = true;
    input.value = String(next);
    this.updateQuantity(input.dataset.index, next, input.getAttribute('name'));
  }

  /**
   * Fetch cart section HTML; tries instance id, liquid name, then full cart page.
   * @returns {Promise<Document|null>}
   */
  async fetchCartSectionDocument() {
    const now = Date.now();
    if (this._sectionFetchBlockedUntil && now < this._sectionFetchBlockedUntil) {
      return null;
    }

    const tryFetch = async (url) => {
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      });
      if (res.status === 403 || res.status === 429) {
        this._sectionFetchBlockedUntil = Date.now() + 15000;
        return null;
      }
      if (!res.ok) return null;
      const text = await res.text();
      return new DOMParser().parseFromString(text, 'text/html');
    };

    const doc = await tryFetch(buildCartSectionFetchUrl(CART_SECTION_ID));
    if (doc) return doc;

    const shopRoutes = getRoutes();
    const cartPageUrl = (shopRoutes && shopRoutes.cart_url) ? shopRoutes.cart_url : '/cart';
    const fullUrl = cartPageUrl.startsWith('http')
      ? cartPageUrl
      : new URL(cartPageUrl.startsWith('/') ? cartPageUrl : '/' + cartPageUrl, window.location.origin).toString();
    return tryFetch(fullUrl);
  }

  /**
   *  Debounced onChange event handler. Updates the cart when the user stops clicking quantity buttons.
   * @param {*} event
   */
  onChange(event) {
    if (this._skipNextQtyChange) {
      this._skipNextQtyChange = false;
      return;
    }
    const input = event.target.closest('[name="updates[]"]') || event.target;
    if (!input || !input.dataset.index) return;
    this.updateQuantity(input.dataset.index, input.value, input.getAttribute('name'));
  }

  getQuantityInput(line) {
    return (
      this.querySelector('[name="updates[]"][data-index="' + line + '"]') ||
      this.querySelector('#Quantity-' + line)
    );
  }

  isValidLineItemKey(key) {
    return typeof key === 'string' && /^\d+:.+/.test(key.trim());
  }

  buildCartChangeBody(identifier, nextQuantity, includeSections) {
    const body = Object.assign({ quantity: nextQuantity }, identifier || {});

    if (includeSections) {
      body.sections = CART_SECTION_ID;
      body.sections_url = getCartPagePathname();
    }

    return body;
  }

  resolveCartLineIdentifier(cartJson, lineIndex, quantityElement) {
    if (cartJson && Array.isArray(cartJson.items) && cartJson.items.length) {
      const itemByIndex = cartJson.items[lineIndex - 1];
      if (itemByIndex && this.isValidLineItemKey(itemByIndex.key)) {
        return { id: itemByIndex.key.trim() };
      }

      const domKey = quantityElement && quantityElement.dataset.lineKey;
      if (this.isValidLineItemKey(domKey)) {
        const matched = cartJson.items.find((item) => item.key === domKey.trim());
        if (matched && this.isValidLineItemKey(matched.key)) {
          return { id: matched.key.trim() };
        }
      }

      if (lineIndex >= 1 && lineIndex <= cartJson.items.length) {
        return { line: lineIndex };
      }

      return null;
    }

    const domKey = quantityElement && quantityElement.dataset.lineKey;
    if (this.isValidLineItemKey(domKey)) {
      return { id: domKey.trim() };
    }

    return { line: lineIndex };
  }

  async resolveCartChangeRequest(lineIndex, nextQuantity, quantityElement) {
    let cartJson = await this.fetchCartJson();
    let identifier = this.resolveCartLineIdentifier(cartJson, lineIndex, quantityElement);

    if (!identifier) {
      const html = await this.fetchCartSectionDocument();
      if (html) {
        this.updateCartSections(html);
      }
      cartJson = await this.fetchCartJson();
      const freshInput = this.getQuantityInput(String(lineIndex));
      identifier = this.resolveCartLineIdentifier(cartJson, lineIndex, freshInput || quantityElement);
    }

    if (!identifier) return null;
    return this.buildCartChangeBody(identifier, nextQuantity, true);
  }

  async fetchCartJson() {
    const shopRoutes = getRoutes();
    const url = cartApiJsUrl(shopRoutes && shopRoutes.cart_url, '/cart');

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText || '{}'));
            return;
          } catch (e) {
            /* fall through */
          }
        }
        resolve(null);
      };
      xhr.onerror = () => resolve(null);
      xhr.send();
    });
  }

  optimisticRemoveLine(line) {
    const root = this.getCartSectionRoot();
    const lineEl =
      root.querySelector('#CartItem-' + line) || this.querySelector('#CartItem-' + line);
    if (lineEl) {
      lineEl.classList.add('is-removing');
      lineEl.style.opacity = '0.45';
      lineEl.style.pointerEvents = 'none';
    }

    const input = this.getQuantityInput(line);
    const removedQty = input ? parseInt(input.value, 10) || 0 : 0;
    if (removedQty > 0) {
      this.currentItemCount = Math.max(0, (this.currentItemCount || 0) - removedQty);
      if (window.FyCardAddToCart && typeof window.FyCardAddToCart.updateCartBadges === 'function') {
        window.FyCardAddToCart.updateCartBadges(this.currentItemCount);
      }
    }
  }

  finalizeRemovedLine(line) {
    const root = this.getCartSectionRoot();
    const lineEl =
      root.querySelector('#CartItem-' + line) || this.querySelector('#CartItem-' + line);
    if (lineEl) lineEl.remove();
  }

  async syncCartStateAfterFormChange(requestSeq) {
    const cartJson = await this.fetchCartJson();
    if (requestSeq !== this._quantityRequestSeq || !cartJson) return;

    if (window.FyCardAddToCart && typeof window.FyCardAddToCart.updateCartBadges === 'function') {
      window.FyCardAddToCart.updateCartBadges(cartJson.item_count);
    }

    this.currentItemCount = cartJson.item_count || 0;
    this.syncShippingFromCartJson(cartJson);
    this.applyCartTotalsFromJson(cartJson);

    if (cartJson.item_count === 0) {
      const html = await this.fetchCartSectionDocument();
      if (html && requestSeq === this._quantityRequestSeq) {
        this.updateCartSections(html);
      }
    }
  }

  getCartChangeUrl() {
    const shopRoutes = getRoutes();
    return cartApiJsUrl(shopRoutes && shopRoutes.cart_change_url, '/cart/change');
  }

  getCartUpdateUrl() {
    const shopRoutes = getRoutes();
    return cartApiJsUrl(shopRoutes && shopRoutes.cart_update_url, '/cart/update');
  }

  cartRequestViaXHR(url, body, contentType) {
    const ct = contentType || 'application/json';
    const payload =
      ct === 'application/json'
        ? JSON.stringify(body)
        : body instanceof URLSearchParams
          ? body.toString()
          : String(body);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', ct);
      xhr.setRequestHeader('Accept', 'application/json, text/html, */*');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.onload = () => {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: async () => {
            try {
              return JSON.parse(xhr.responseText || '{}');
            } catch (e) {
              return {};
            }
          },
        });
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(payload);
    });
  }

  async cartPostJson(url, body) {
    const init = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    };

    try {
      const response = await fetch(url, init);
      if (response.status !== 403 && response.status !== 429) {
        return response;
      }
    } catch (e) {
      /* fall through to XHR */
    }

    return this.cartRequestViaXHR(url, body);
  }

  async postCartChangeForm(lineIndex, nextQuantity, quantityElement) {
    const shopRoutes = getRoutes();
    const url = cartHtmlUrl(shopRoutes && shopRoutes.cart_change_url, '/cart/change');
    const params = new URLSearchParams();
    params.set('quantity', String(nextQuantity));

    const cartJson = await this.fetchCartJson();
    const identifier = this.resolveCartLineIdentifier(cartJson, lineIndex, quantityElement);
    if (!identifier) {
      params.set('line', String(lineIndex));
    } else if (identifier.id) {
      params.set('id', identifier.id);
    } else {
      params.set('line', String(identifier.line));
    }

    return this.cartRequestViaXHR(
      url,
      params,
      'application/x-www-form-urlencoded; charset=UTF-8'
    );
  }

  async postCartChange(body) {
    const shopRoutes = getRoutes();
    const url = cartApiJsUrl(shopRoutes && shopRoutes.cart_change_url, '/cart/change');
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
  }

  async postCartUpdateByLine(lineIndex, nextQuantity) {
    const updates = {};
    updates[String(lineIndex)] = nextQuantity;
    return this.cartPostJson(this.getCartUpdateUrl(), { updates: updates });
  }

  async applyCartChangeResponse(line, lineIndex, nextQuantity, name, quantityElement, responseJson) {
    if (responseJson.item_count != null) {
      this.currentItemCount = responseJson.item_count;
      if (window.FyCardAddToCart && typeof window.FyCardAddToCart.updateCartBadges === 'function') {
        window.FyCardAddToCart.updateCartBadges(responseJson.item_count);
      }
    }

    const lineKey = quantityElement && quantityElement.dataset.lineKey;
    const serverLine =
      (responseJson.items && lineKey && responseJson.items.find((item) => item.key === lineKey)) ||
      (responseJson.items && responseJson.items[lineIndex - 1]);

    if (nextQuantity === 0 || !serverLine) {
      this.finalizeRemovedLine(line);
    } else {
      const confirmedQty = serverLine.quantity;
      this.setQuantityInputValue(line, confirmedQty);

      if (quantityElement && serverLine.key) {
        quantityElement.dataset.lineKey = serverLine.key;
      }
    }

    document.dispatchEvent(new CustomEvent('cart:update', { detail: responseJson }));

    this.syncShippingFromCartJson(responseJson);
    this.applyCartTotalsFromJson(responseJson);

    if (serverLine && nextQuantity !== 0) {
      this.applyLinePricesFromJson(responseJson, lineIndex);
    }

    const sectionHtml =
      responseJson.sections &&
      (responseJson.sections[CART_SECTION_ID] || responseJson.sections['main-cart']);

    if (sectionHtml) {
      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      this.updateCartSections(html);
      return;
    }

    if (responseJson.item_count === 0) {
      const html = await this.fetchCartSectionDocument();
      if (html) this.updateCartSections(html);
    }
  }

  isCartChangeError(response, responseJson) {
    if (!response.ok) return true;
    if (!responseJson || typeof responseJson !== 'object') return true;
    if (responseJson.errors) return true;
    if (typeof responseJson.status === 'number' && responseJson.status >= 400) return true;
    if (responseJson.status === 'bad_request' || responseJson.status === 'error') return true;
    return false;
  }

  isInvalidLineCartError(responseJson) {
    const message =
      (responseJson && (responseJson.description || responseJson.message)) || '';
    return /no valid id or line|line parameter is invalid|invalid line/i.test(String(message));
  }

  syncLineKeysFromCart(cartJson) {
    if (!cartJson || !Array.isArray(cartJson.items)) return;

    cartJson.items.forEach((item, index) => {
      const line = index + 1;
      const input = this.getQuantityInput(line);
      if (input && item.key) {
        input.dataset.lineKey = item.key;
      }
    });
  }

  /**
   * Returns an array of sections to render when the cart is updated. Limit: 5 sections.
   * Does not make the request to the server, only returns the section names to render.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Array} Array of sections to render
   */
  getSectionItemsToRender() {
    return [
      'cart-items',
      'cart-shipping-notification',
      'cart-recommendations',
      'cart-note',
      'cart-footer',
    ];
  }

  /** Scope DOM updates to the cart page or drawer that contains this <cart-items> */
  getCartSectionRoot() {
    return (
      this.closest('#drawer-cart') ||
      this.closest('.fy-cart-page-section') ||
      this.closest('.shopify-section') ||
      document
    );
  }

  getSectionHtmlRoot(newHTML) {
    return (
      newHTML.getElementById('shopify-section-' + CART_SECTION_ID) ||
      newHTML.getElementById('shopify-section-main-cart') ||
      newHTML.body
    );
  }

  /**
   * This function is always called when the cart is updated.
   * @returns {boolean} whether any block was updated
   */
  updateCartSections(newHTML) {
    const root = this.getCartSectionRoot();
    const sourceRoot = this.getSectionHtmlRoot(newHTML);
    let updated = false;

    this.getSectionItemsToRender().forEach((item) => {
      if (item === 'cart-items') {
        const sourceItems = sourceRoot.querySelector('cart-items') || newHTML.querySelector('cart-items');
        if (sourceItems) {
          this.innerHTML = sourceItems.innerHTML;
          updated = true;
        }
        return;
      }

      const elementToReplace = root.querySelector('#' + item);
      const source = sourceRoot.querySelector('#' + item) || newHTML.getElementById(item);

      if (!elementToReplace || !source) return;

      elementToReplace.innerHTML = source.innerHTML;
      updated = true;

      if (item === 'cart-shipping-notification') {
        this.updateShippingNotification('cart-shipping-notification');
      }
    });

    if (this.updateDiscountBlocks(sourceRoot)) {
      updated = true;
    }

    return updated;
  }

  /** Section HTML only includes #cart-discount-code; mirror into drawer block when present */
  updateDiscountBlocks(sourceRoot) {
    const source =
      sourceRoot.querySelector('#cart-discount-code') ||
      sourceRoot.querySelector('#cart-discount-code-drawer');
    if (!source) return false;

    const root = this.getCartSectionRoot();
    let updated = false;

    ['cart-discount-code', 'cart-discount-code-drawer'].forEach((blockId) => {
      const target = root.querySelector('#' + blockId);
      if (!target) return;

      target.innerHTML = source.innerHTML;

      if (blockId === 'cart-discount-code-drawer') {
        const input = target.querySelector('[data-fy-discount-input]');
        const label = target.querySelector('label[for]');
        if (input) input.id = 'fy-discount-input-drawer';
        if (label) label.setAttribute('for', 'fy-discount-input-drawer');
      } else {
        const input = target.querySelector('[data-fy-discount-input]');
        const label = target.querySelector('label[for]');
        if (input) input.id = 'fy-discount-input-page';
        if (label) label.setAttribute('for', 'fy-discount-input-page');
      }

      updated = true;
    });

    return updated;
  }

  syncShippingFromCartJson(cartJson) {
    if (!cartJson || cartJson.total_price == null) return;

    const root = this.getCartSectionRoot();
    const shippingNotificationWrapper = root.querySelector('#cart-shipping-notification');
    if (!shippingNotificationWrapper) return;

    const shippingNotification = shippingNotificationWrapper.querySelector('[data-free-shipping-notification]');
    if (shippingNotification) {
      shippingNotification.dataset.cartTotal = String(cartJson.total_price);
    }

    this.updateShippingNotification('cart-shipping-notification');
  }

  applyLinePricesFromJson(cartJson, lineIndex) {
    if (!cartJson || !cartJson.items || !lineIndex) return;

    const root = this.getCartSectionRoot();
    const lineEl = root.querySelector('#CartItem-' + lineIndex);
    const item = cartJson.items[lineIndex - 1];
    if (!lineEl || !item) return;

    const priceEl = lineEl.querySelector('.fy-cart-item__price');
    const compareEl = lineEl.querySelector('.fy-cart-item__compare');
    const savingsEl = lineEl.querySelector('.fy-cart-item__savings');
    const lineSavings = (item.original_line_price || 0) - (item.final_line_price || 0);

    if (priceEl) setMoneyOnElement(priceEl, item.final_line_price);
    if (compareEl && item.original_line_price !== item.final_line_price) {
      setMoneyOnElement(compareEl, item.original_line_price);
      compareEl.hidden = false;
    } else if (compareEl) {
      compareEl.hidden = true;
    }
    if (savingsEl) {
      if (lineSavings > 0) {
        savingsEl.hidden = false;
        savingsEl.textContent = '(You save ' + formatCartMoney(lineSavings) + ')';
      } else {
        savingsEl.hidden = true;
      }
    }
  }

  applyCartTotalsFromJson(cartJson) {
    if (!cartJson || cartJson.total_price == null) return;

    const root = this.getCartSectionRoot();
    const footer = root.querySelector('#cart-footer');
    if (!footer) return;

    const totalEl = footer.querySelector('.fy-cart-summary__row--total span:last-child');
    const savingsRow = footer.querySelector('.fy-cart-summary__row--savings');
    const savingsEl = savingsRow && savingsRow.querySelector('span:last-child');
    const totalSavings = (cartJson.original_total_price || 0) - (cartJson.total_price || 0);

    if (totalEl) {
      setMoneyOnElement(totalEl, cartJson.total_price);
    }

    if (savingsRow && savingsEl) {
      if (totalSavings > 0) {
        savingsRow.hidden = false;
        setMoneyOnElement(savingsEl, totalSavings, '-');
      } else {
        savingsRow.hidden = true;
      }
    }
  }

  /**
   * This function handles multi-currency support for the shipping notification bar.
   * This is called when item is added to cart or when the cart is updated.
   * @param {string} selector
   * @param {string*} json - Cart JSON
   */
  updateShippingNotification(selector) {
    if (this._updatingShipping) {
      return;
    }
    this._updatingShipping = true;

    const root = this.getCartSectionRoot();
    const shippingNotificationWrapper =
      root.querySelector('#' + selector) || document.getElementById(selector);

    if (!shippingNotificationWrapper) {
      this._updatingShipping = false;
      return;
    }

    const shippingNotification = shippingNotificationWrapper.querySelector('[data-free-shipping-notification]');
    if (!shippingNotification) {
      this._updatingShipping = false;
      return;
    }

    const messageEl = shippingNotification.querySelector('.free-shipping-message');
    const fill = shippingNotificationWrapper.querySelector('[data-fy-shipping-fill]');
    const truck = shippingNotificationWrapper.querySelector('[data-fy-shipping-truck]');

    let cartTotal = parseInt(shippingNotification.dataset.cartTotal, 10);
    const freeShippingTreshold = parseInt(shippingNotification.dataset.threshold, 10);
    const shopCurrency = shippingNotification.dataset.shopCurrency;
    const targetCurrency = shippingNotification.dataset.targetCurrency;

    if (shopCurrency && targetCurrency && shopCurrency !== targetCurrency) {
      cartTotal = Math.round(cartTotal / (parseFloat(Shopify.currency.rate) || 1));
    }

    const pct = freeShippingTreshold > 0 ? Math.min(100, Math.round((cartTotal / freeShippingTreshold) * 100)) : 0;

    if (fill) {
      fill.style.width = pct + '%';
    }
    if (truck) {
      truck.hidden = pct < 100;
    }

    if (messageEl) {
      if (cartTotal >= freeShippingTreshold) {
        shippingNotification.classList.add('threshold-reached');
        const unlocked =
          (window.fyCartDrawerStrings && window.fyCartDrawerStrings.shippingUnlocked) ||
          window.cartStrings.shipping_free;
        messageEl.textContent = unlocked;
      } else {
        const missingShop = freeShippingTreshold - cartTotal;
        shippingNotification.classList.remove('threshold-reached');

        if (window.cartStrings && window.cartStrings.shipping_not_free) {
          const amountHtml =
            typeof Woolman !== 'undefined' && Woolman.Utils && Woolman.Utils.formatMoney
              ? Woolman.Utils.formatMoney(missingShop)
              : formatCartMoney(missingShop);
          messageEl.innerHTML = window.cartStrings.shipping_not_free.replace('[amount]', amountHtml);
        }
      }
    }

    this._updatingShipping = false;
  }

  /**
   * Fetches fresh sections from the server and updates the cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderItems() {
    try {
      const html = await this.fetchCartSectionDocument();
      if (html) this.updateCartSections(html);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Fetches fresh sections from the server and updates the cart. This is called when item is added to cart.
   */
  async renderAndShowItems(evt) {
    return CartItems.handleAjaxProductAdded(evt);
  }

  static async handleAjaxProductAdded(evt) {
    if (CartItems._renderInProgress) return;
    CartItems._renderInProgress = true;

    const instances = Array.from(document.querySelectorAll('cart-items'));
    const leader = instances[0];
    if (!leader) {
      CartItems._renderInProgress = false;
      return;
    }

    const skipDrawer = evt && evt.detail && evt.detail.skipDrawer;
    const prefersDrawer = window.matchMedia('(min-width: 768px)').matches;

    try {
      const cartJson = evt && evt.detail && evt.detail.cart;
      let sectionDoc = null;

      if (cartJson && cartJson.sections && cartJson.sections[CART_SECTION_ID]) {
        sectionDoc = new DOMParser().parseFromString(cartJson.sections[CART_SECTION_ID], 'text/html');
      } else {
        sectionDoc = await leader.fetchCartSectionDocument();
      }

      if (sectionDoc) {
        instances.forEach((el) => el.updateCartSections(sectionDoc));
      } else if (cartJson) {
        instances.forEach((el) => {
          el.syncLineKeysFromCart(cartJson);
          el.syncShippingFromCartJson(cartJson);
          el.applyCartTotalsFromJson(cartJson);
        });
      }

      const drawer = document.getElementById('drawer-cart');
      if (
        prefersDrawer &&
        drawer &&
        !skipDrawer &&
        typeof Woolman !== 'undefined' &&
        Woolman.ModalsAndDrawers
      ) {
        Woolman.ModalsAndDrawers.showModalOrDrawer(drawer.getAttribute('id') || 'drawer-cart');
      }
    } catch (e) {
      console.error(e);
    } finally {
      CartItems._renderInProgress = false;
    }
  }

  /**
   * Updates the quantity of a line item in the cart. This is called when the user changes the quantity of an item in the cart.
   * @param {*} line
   * @param {*} quantity
   * @param {*} name
   */
  updateQuantity(line, quantity, name) {
    const lineIndex = parseInt(line, 10);
    const nextQuantity = parseInt(quantity, 10);
    if (isNaN(lineIndex) || lineIndex < 1 || isNaN(nextQuantity) || nextQuantity < 0) return;

    if (!getRoutes()) {
      console.error('[cart-items] Theme scripts not ready (window.routes missing)');
      return;
    }

    const quantityElement = this.getQuantityInput(line);
    const previousValue = quantityElement
      ? quantityElement.dataset.value || quantityElement.getAttribute('value') || quantityElement.value
      : quantity;

    const requestSeq = ++this._quantityRequestSeq;

    this._quantityUpdateQueue = this._quantityUpdateQueue
      .then(() => this._performQuantityUpdate(line, lineIndex, nextQuantity, name, previousValue, requestSeq))
      .catch((error) => {
        console.error(error);
        if (requestSeq === this._quantityRequestSeq) {
          this.setQuantityInputValue(line, previousValue);
        }
      });

    return this._quantityUpdateQueue;
  }

  submitCartFormFallback(line, nextQuantity) {
    const now = Date.now();
    if (!CartItems._lastFormSubmitAt) CartItems._lastFormSubmitAt = 0;
    if (now - CartItems._lastFormSubmitAt < 2500) return false;

    const root = this.getCartSectionRoot();
    let form = root.querySelector('form#cart');
    if (!form) {
      form = document.querySelector('form#cart');
    }
    if (!form) return false;

    this.setQuantityInputValue(line, nextQuantity);

    let updateField = form.querySelector('[name="update"]');
    if (!updateField) {
      updateField = document.createElement('input');
      updateField.type = 'hidden';
      updateField.name = 'update';
      updateField.value = '';
      form.appendChild(updateField);
    }

    CartItems._lastFormSubmitAt = now;
    form.submit();
    return true;
  }

  async refreshCartFromSection(line, lineIndex, name, previousValue, nextQuantity, requestSeq) {
    const html = await this.fetchCartSectionDocument();
    if (requestSeq !== this._quantityRequestSeq) return;

    if (html) {
      this.updateCartSections(html);
      const syncedInput =
        html.querySelector('[name="updates[]"][data-index="' + line + '"]') ||
        html.querySelector('#Quantity-' + line);
      if (syncedInput) {
        this.setQuantityInputValue(line, syncedInput.value);
      }
      document.dispatchEvent(new CustomEvent('cart:update'));
      return;
    }

    if (
      document.documentElement.classList.contains('template-cart') &&
      this.submitCartFormFallback(line, nextQuantity)
    ) {
      return;
    }

    this.setQuantityInputValue(line, previousValue);
    this.updateLiveRegions(
      line,
      'Unable to refresh cart. Try refreshing the page, or temporarily disable loyalty/cart apps (Bloy, Smile.io).'
    );
  }

  async _performQuantityUpdate(line, lineIndex, nextQuantity, name, previousValue, requestSeq) {
    this.enableLoading(line);

    try {
      const quantityElement = this.getQuantityInput(line);
      let changeBody = await this.resolveCartChangeRequest(lineIndex, nextQuantity, quantityElement);

      if (!changeBody) {
        this.setQuantityInputValue(line, previousValue);
        this.updateLiveRegions(line, 'Cart item not found. Please refresh the page.');
        return;
      }

      let response = await this.postCartChange(changeBody);
      let responseJson = await response.json();

      if (requestSeq !== this._quantityRequestSeq) return;

      if (this.isCartChangeError(response, responseJson) && this.isInvalidLineCartError(responseJson)) {
        const html = await this.fetchCartSectionDocument();
        if (html && requestSeq === this._quantityRequestSeq) {
          this.updateCartSections(html);
        }
        changeBody = await this.resolveCartChangeRequest(
          lineIndex,
          nextQuantity,
          this.getQuantityInput(line)
        );
        if (changeBody && requestSeq === this._quantityRequestSeq) {
          response = await this.postCartChange(changeBody);
          responseJson = await response.json();
        }
      }

      if (requestSeq !== this._quantityRequestSeq) return;

      if (this.isCartChangeError(response, responseJson)) {
        this.setQuantityInputValue(line, previousValue);
        let message =
          (responseJson && (responseJson.description || responseJson.message)) ||
          responseJson.errors ||
          'Cart update failed';
        if (typeof message !== 'string') message = 'Cart update failed';
        this.updateLiveRegions(line, message);
        return;
      }

      this._cartApiBlocked = false;
      await this.applyCartChangeResponse(
        line,
        lineIndex,
        nextQuantity,
        name,
        quantityElement,
        responseJson
      );
    } finally {
      this.disableLoading(line);
    }
  }

  /**
   * Updates the live regions for the cart.
   * @param {*} line
   * @param {*} message
   */
  updateLiveRegions(line, message) {
    const root = this.getCartSectionRoot();
    const lineItemError =
      root.querySelector('#Line-item-error-' + line) ||
      root.querySelector('#CartDrawer-LineItemError-' + line);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      lineItemError.classList.remove('hide');
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  setQuantityInputValue(line, value) {
    const str = String(value);
    const input = this.getQuantityInput(line);
    if (input) {
      input.value = str;
      input.dataset.value = str;
    }
  }

  enableLoading(line) {
    const root = this.getCartSectionRoot();
    const lineItem = root.querySelector('#CartItem-' + line) || this.querySelector('#CartItem-' + line);
    if (lineItem) {
      lineItem.classList.add('is-processing');
      lineItem.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.remove('hidden'));
    }
  }

  disableLoading(line) {
    const items = line
      ? [this.querySelector('#CartItem-' + line), this.getCartSectionRoot().querySelector('#CartItem-' + line)]
      : Array.from(this.querySelectorAll('.cart-item'));
    items.forEach((lineItem) => {
      if (!lineItem) return;
      lineItem.classList.remove('is-processing');
      lineItem.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
    });
    this.classList.remove('is-processing');
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', cartDebounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(cartApiJsUrl(routes.cart_update_url, '/cart/update'), {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', this.loadRecommendations.bind(this))
    }

    async loadRecommendations() {
      if (!this.dataset.productId || this.dataset.productId === '') return;

      try {
        const url = new URL(location.origin + routes.product_recommendations_url + '.json')
        url.searchParams.set('product_id', this.dataset.productId)
        url.searchParams.set('limit', this.dataset.limit || 10)

        const res = await fetch(url)
        const { products } = await res.json();

        if (!products || products.length == 0) {
          throw 'No product recommendations found for products in the cart'
        }

        this.productsContainer = this.querySelector('[data-products-ajax]')
        this.productsContainer.innerHTML = '';

        products.forEach((product) => {

          const template = this.querySelector('#template-recommendation-item').content.firstElementChild.cloneNode(true);
          const filename = product.featured_image.split('/').pop();
          const lastDotIndex = filename.lastIndexOf('.');
          const newFilename = `${filename.substring(0, lastDotIndex)}_300x${filename.substring(lastDotIndex)}`;
          const imgUrl = product.featured_image.replace(filename, newFilename);
          const productUrl = location.origin + product.url;

          template.querySelector('img').setAttribute('src', imgUrl);
          template.querySelector('img').setAttribute('alt', product.title);
          template.querySelector('.card-media').setAttribute('href', productUrl);
          template.setAttribute('title', product.title);
          template.querySelector('.card-title a').textContent = product.title;
          template.querySelector('.card-title a').setAttribute('href', productUrl);

          const priceEl = template.querySelector('.fy-rec-item__price');
          const compareEl = template.querySelector('.fy-rec-item__compare');
          if (priceEl && typeof Woolman !== 'undefined' && Woolman.Utils && Woolman.Utils.formatMoney) {
            setMoneyOnElement(priceEl, product.price);
          }
          if (compareEl && product.compare_at_price && product.compare_at_price > product.price && Woolman.Utils && Woolman.Utils.formatMoney) {
            setMoneyOnElement(compareEl, product.compare_at_price);
            compareEl.hidden = false;
          }

          const addBtn = template.querySelector('.fy-rec-item__add');
          if (addBtn) {
            const variant = product.variants && product.variants.find((v) => v.available);
            if (variant) {
              addBtn.addEventListener('click', () => {
                addBtn.disabled = true;
                fetch(cartApiJsUrl(routes.cart_add_url, '/cart/add'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
                })
                  .then((r) => r.json())
                  .then((data) => {
                    if (data.status) {
                      console.warn(data.description || data.message);
                      return;
                    }
                    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { skipDrawer: true } }));
                  })
                  .catch((e) => console.error(e))
                  .finally(() => { addBtn.disabled = false; });
              });
            } else {
              addBtn.disabled = true;
            }
          }

          this.productsContainer.appendChild(template);
        })
      } catch(error) {
        this.setAttribute('hidden', true)
        console.warn(error)
      }
    }
  })
}